import { combineReducers } from '@reduxjs/toolkit'

import login from './slices/auth/loginSlice'
import logout from './slices/auth/logoutSlice'
import otpReducer from './slices/auth/otpSlice'
import s3urlRequest from './slices/s3urlRequest'
import chatHistory from './slices/ai-chat/history'
import sourceList, { ExportSourceListSlice, DeleteSourceSlice } from './slices/ai-chat/sourceList'
import getCatalogues from './slices/ai-chat/prompts'
import documentSearch from './slices/ai-chat/doc-research'
import forgotPassword from './slices/auth/forgotPassword'
import resetPassword from './slices/auth/resetPassword'
import signUp from './slices/auth/signupSlice'
import fetchCompanies from './slices/ai-chat/fetchCompanies'
import getFolderStructure from './slices/custom-research/folder-structure'
import { UploadFileSlice, DeleteFileSlice } from './slices/custom-research/upload-file'
import { ESGChatSlice, ESGChatFeedbackSlice, ESGChatFoldersSlice, ESGChatSignedUrlSlice, ESGChatControversySlice } from './slices/ai-chat/esgChat'
import maxUserLimit from './slices/internal/maxLimit'
import syncNow from './slices/custom-research/SyncNow'
import { requestPlaybookSlice, createPlaybookSlice, retrievePlaybookSlice, viewPlaybookSlice, deletePlaybookSlice } from './slices/manage-playbook/playbook'
import { createPlaybookBatchSlice, PrefillPlaybookSourceYearSlice } from './slices/manage-playbook/playbookBatch'
import { importMasterQuestionsSlice } from './slices/manage-playbook/questions'
import { playbookNameDropdownSlice } from './slices/manage-playbook/playbookNameDropdown'
import { disclosureTypesSlice } from './slices/playbook-batch/disclosureTypes'
import { retrieveBatchSlice } from './slices/playbook-batch/retrieveBatch'
import { exportQuestionsSlice } from './slices/manage-playbook/exportQuestions'
import { reportExportSlice } from './slices/playbook-batch/reportExport'
import { playbookBatchQuestionsSlice } from './slices/playbook-batch/viewPlaybookQuestions'
import { questionWiseUpdateSlice } from './slices/playbook-batch/updateQuestionData'
import { uploadEntitiesSlice } from './slices/entities/uploadEntities'
import { exportEntitiesSlice, retrieveEntitiesSlice } from './slices/entities/retrieveEntities'
import { manageEntitiesFilterSlice } from './slices/entities/entitiesFilters'
import { deleteEntitiesSlice, editEntitiesSlice } from './slices/entities/updateEntities'
import { addPlaybookQuestionsSlice, editPlaybookQuestionsSlice } from './slices/manage-playbook/editPlaybookQuestions'
import { getPrioritizedSourceSlice, updateSourcePrioritySlice } from './slices/manage-playbook/prioritizeSource'
import authDetails from "./slices/auth/authDetails";
import updateSites from "./slices/sync-external/customSources";
import siteDetails from "./slices/sync-external/siteDetails";
import { getUserFolderDetailsSlice } from './slices/playbook-batch/userWiseFolderList'
import controList from "./slices/contro-research/contro-list"
import { DownloadFailedFileSlice, FilesDataSlice } from './slices/gemini/failed-files'
import { entityFilesListSlice, entityListSlice, GetEntityLevelDataSlice, GetItemLevelDataSlice } from './slices/gemini/get-entities'
import { CompleteReviewSlice, ReviewFileSlice } from './slices/gemini/review-file'
import { promptFiltersDataSlice } from './slices/gemini/prompt-filters'
import { extractVariableSlice } from './slices/gemini/extract-variable'
import { exportExtractionSlice } from './slices/gemini/export-extraction'
import { FilterValueNavigation } from './slices/gemini/filter-value'
import { ImportCorrectedFileSlice } from './slices/gemini/import-corrected-file'
import { PublishTaskActionSlice } from './slices/gemini/publish-task'
import { DeleteUploadedFileSlice } from './slices/gemini/delete-uploaded-file'
import { InitiateSyncSlice } from './slices/gemini/file-sync'
import { ExportEntitiesActionSlice } from './slices/gemini/export-entities'
import { FileValidationActionSlice } from './slices/gemini/file-validation'
import { UpdateDocsDataSlice } from './slices/gemini/update-docs-data'
import { UpdateDocsFormDataSlice } from './slices/gemini/update-docs-formdata'
import { DocReviewExportSlice } from './slices/gemini/doc-review-export'
import { DownloadMetaJsonSlice } from './slices/gemini/download-meta-json'
import viewMetrics from './slices/gemini/view-metrics'
import improvePrompt from './slices/ai-chat/prompt-genie'
import { GetTourListSlice, RemoveToursSlice } from './slices/generics/tourSlice'
import { CreatePortfolioSlice } from './slices/portfolio/createPortfolio'
import { RetrievePortfolioDataSlice } from './slices/portfolio/retrievePortfolio'
import { ChunkEvaluationSlice } from './slices/ai-chat/chunk-evaluation'

// alphabetical-wise
const combinedReducer = combineReducers({
    addPlaybookQuestions: addPlaybookQuestionsSlice.reducer,
    authDetails,
    catalogues: getCatalogues,
    chatFeedback: ESGChatFeedbackSlice.reducer,
    chatFolders: ESGChatFoldersSlice.reducer,
    chatSignedUrl: ESGChatSignedUrlSlice.reducer,
    chatHistory: chatHistory,
    chunkEvaluation: ChunkEvaluationSlice.reducer,
    completeReview: CompleteReviewSlice.reducer,
    controList,
    createPlaybook: createPlaybookSlice.reducer,
    createPlaybookBatch: createPlaybookBatchSlice.reducer,
    createPortfolio: CreatePortfolioSlice.reducer,
    deleteEntities: deleteEntitiesSlice.reducer,
    deleteFile: DeleteFileSlice.reducer,
    deletePlaybook: deletePlaybookSlice.reducer,
    deleteSource: DeleteSourceSlice?.reducer,
    deleteUploadedFile: DeleteUploadedFileSlice.reducer,
    disclosureTypes: disclosureTypesSlice.reducer,
    docReviewExport: DocReviewExportSlice.reducer,
    documentSearch,
    downloadFailedFile: DownloadFailedFileSlice.reducer,
    downloadMetaJson: DownloadMetaJsonSlice.reducer,
    editEntities: editEntitiesSlice.reducer,
    entityList: entityListSlice.reducer,
    entityFilesList: entityFilesListSlice.reducer,
    exportEntities: exportEntitiesSlice.reducer,
    exportEntitiesData: ExportEntitiesActionSlice.reducer,
    exportQuestions: exportQuestionsSlice.reducer,
    editPlaybookQuestions: editPlaybookQuestionsSlice.reducer,
    esgChat: ESGChatSlice.reducer,
    esgChatControversy: ESGChatControversySlice.reducer,
    exportSourceList: ExportSourceListSlice.reducer,
    extractVariable: extractVariableSlice.reducer,
    exportExtraction: exportExtractionSlice.reducer,
    filesData: FilesDataSlice.reducer,
    fileValidation: FileValidationActionSlice.reducer,
    filterValue: FilterValueNavigation.reducer,
    fetchCompanies: fetchCompanies,
    fetchFolders: getFolderStructure,
    forgotPassword,
    getEntityLevelData: GetEntityLevelDataSlice.reducer,
    getPrioritizedSource: getPrioritizedSourceSlice.reducer,
    importCorrectedFile: ImportCorrectedFileSlice.reducer,
    importMasterQuestions: importMasterQuestionsSlice.reducer,
    initiateSync: InitiateSyncSlice.reducer,
    itemLevelData: GetItemLevelDataSlice.reducer,
    login,
    logout,
    manageEntitiesFilter: manageEntitiesFilterSlice.reducer,
    maxUserLimit,
    otp: otpReducer,
    playbookBatchQuestions: playbookBatchQuestionsSlice.reducer,
    playbookNameDropdown: playbookNameDropdownSlice.reducer,
    prefillPlaybookSourceYear: PrefillPlaybookSourceYearSlice?.reducer,
    promptFilters: promptFiltersDataSlice?.reducer,
    publishTask: PublishTaskActionSlice.reducer,
    questionWiseUpdate: questionWiseUpdateSlice.reducer,
    resetPassword,
    reportExport: reportExportSlice.reducer,
    requestPlaybook: requestPlaybookSlice.reducer,
    retrieveBatch: retrieveBatchSlice.reducer,
    retrieveEntities: retrieveEntitiesSlice.reducer,
    retrievePlaybook: retrievePlaybookSlice.reducer,
    reviewFile: ReviewFileSlice.reducer,
    retrievePortfolio: RetrievePortfolioDataSlice.reducer,
    signUp,
    siteDetails,
    s3urlRequest,
    sourceList,
    syncNow,
    updateSites,
    updateDocsData: UpdateDocsDataSlice.reducer,
    updateDocsFormData: UpdateDocsFormDataSlice.reducer,
    updateSourcePriority: updateSourcePrioritySlice.reducer,
    uploadEntities: uploadEntitiesSlice.reducer,
    uploadFile: UploadFileSlice.reducer,
    viewMetrics,
    userWiseFolderList: getUserFolderDetailsSlice.reducer,
    viewPlaybook: viewPlaybookSlice.reducer,
    improvePrompt,
    tourList: GetTourListSlice.reducer,
    endTour: RemoveToursSlice.reducer,
})

const RootReducer = (state, action) => {
    if (action.type === 'RESET') {
        state = undefined
        sessionStorage.clear()
    }
    return combinedReducer(state, action)
}

export default RootReducer
